import { type Geometry, MIN_SIZE } from "../workspace/schema.js";

type Mode = "move" | "resize";

/**
 * Drag and resize, written to hold 60fps (§9): the gesture writes geometry
 * straight to the element's style inside one rAF per frame, and the workspace
 * document is touched exactly once, on release. A CRDT update per pointermove
 * would be both slow locally and a flood on the wire.
 */
export function beginWindowGesture(options: {
  event: PointerEvent;
  handle: HTMLElement;
  element: HTMLElement;
  mode: Mode;
  geometry: Geometry;
  commit: (geometry: Geometry) => void;
}): void {
  const { event, handle, element, mode, geometry, commit } = options;
  if (event.button !== 0) return;
  event.preventDefault();
  event.stopPropagation();

  const startX = event.clientX;
  const startY = event.clientY;
  let next = geometry;
  let frame = 0;

  const paint = (): void => {
    frame = 0;
    element.style.left = `${next.x}px`;
    element.style.top = `${next.y}px`;
    element.style.width = `${next.w}px`;
    element.style.height = `${next.h}px`;
  };

  const onMove = (move: PointerEvent): void => {
    const dx = move.clientX - startX;
    const dy = move.clientY - startY;
    next =
      mode === "move"
        ? { ...geometry, x: Math.max(0, geometry.x + dx), y: Math.max(0, geometry.y + dy) }
        : {
            ...geometry,
            w: Math.max(MIN_SIZE.w, geometry.w + dx),
            h: Math.max(MIN_SIZE.h, geometry.h + dy),
          };
    if (frame === 0) frame = requestAnimationFrame(paint);
  };

  const onUp = (): void => {
    if (frame !== 0) cancelAnimationFrame(frame);
    paint();
    handle.removeEventListener("pointermove", onMove);
    handle.removeEventListener("pointerup", onUp);
    handle.removeEventListener("pointercancel", onUp);
    handle.releasePointerCapture?.(event.pointerId);
    document.body.style.cursor = "";
    if (next !== geometry) commit(next);
  };

  handle.setPointerCapture(event.pointerId);
  handle.addEventListener("pointermove", onMove);
  handle.addEventListener("pointerup", onUp);
  handle.addEventListener("pointercancel", onUp);
  document.body.style.cursor = mode === "move" ? "grabbing" : "nwse-resize";
}

/**
 * In a tiled room a window has no free geometry, so dragging it means "put it
 * where that other one is". The element follows the pointer for feedback and
 * snaps back; the drop point decides the swap.
 */
export function beginTileGesture(options: {
  event: PointerEvent;
  handle: HTMLElement;
  element: HTMLElement;
  drop: (clientX: number, clientY: number) => void;
}): void {
  const { event, handle, element, drop } = options;
  if (event.button !== 0) return;
  event.preventDefault();
  event.stopPropagation();

  const startX = event.clientX;
  const startY = event.clientY;
  let lastX = startX;
  let lastY = startY;
  let frame = 0;
  let dragging = false;

  const paint = (): void => {
    frame = 0;
    element.style.transform = `translate(${lastX - startX}px, ${lastY - startY}px)`;
  };

  const onMove = (move: PointerEvent): void => {
    lastX = move.clientX;
    lastY = move.clientY;
    if (!dragging && Math.hypot(lastX - startX, lastY - startY) < 4) return;
    if (!dragging) {
      dragging = true;
      element.style.opacity = "0.85";
      element.style.pointerEvents = "none";
    }
    if (frame === 0) frame = requestAnimationFrame(paint);
  };

  const onUp = (): void => {
    if (frame !== 0) cancelAnimationFrame(frame);
    handle.removeEventListener("pointermove", onMove);
    handle.removeEventListener("pointerup", onUp);
    handle.removeEventListener("pointercancel", onUp);
    handle.releasePointerCapture?.(event.pointerId);
    element.style.transform = "";
    element.style.opacity = "";
    element.style.pointerEvents = "";
    document.body.style.cursor = "";
    if (dragging) drop(lastX, lastY);
  };

  handle.setPointerCapture(event.pointerId);
  handle.addEventListener("pointermove", onMove);
  handle.addEventListener("pointerup", onUp);
  handle.addEventListener("pointercancel", onUp);
  document.body.style.cursor = "grabbing";
}
